import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { news } from '../data/mock';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const News = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const { t, language } = useLanguage();
  
  // Category mapping for translations
  const categoryMap = {
    'All': t('all'),
    'Competition Results': t('competitionResultsCat'),
    'Team Selection': t('teamSelection'),
    'Competition Updates': t('competitionUpdates')
  };
  
  const categories = ['All', ...new Set(news.map((item) => item.category))];

  const filteredNews =
    selectedCategory === 'All'
      ? news
      : news.filter((item) => item.category === selectedCategory);

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section
        className="relative h-96 flex items-center justify-center overflow-hidden"
      >
        {/* B&W Background */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_b6c20734-bb90-4750-94f8-baaefceffec0/artifacts/wo9vqaam_5507DD55-6892-471D-83C5-6B99C46A1C24.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'grayscale(100%)'
          }}
        ></div>
        {/* Color Subject Overlay */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_b6c20734-bb90-4750-94f8-baaefceffec0/artifacts/wo9vqaam_5507DD55-6892-471D-83C5-6B99C46A1C24.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            maskImage: 'radial-gradient(ellipse 45% 55% at 50% 50%, black 20%, transparent 60%)',
            WebkitMaskImage: 'radial-gradient(ellipse 45% 55% at 50% 50%, black 20%, transparent 60%)'
          }}
        ></div>
        <div className="absolute inset-0 bg-black/30"></div>
        {/* Vignette Effect */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 30%, rgba(0,0,0,0.8) 70%, rgba(0,0,0,0.95) 100%)'
          }}
        ></div>
        <div className="relative z-10 text-center text-white px-4 pt-16">
          <h1 className="text-5xl md:text-6xl font-black mb-4">{t('latestNews').split(' ')[0]} <span className="text-pink-500">{t('latestNews').split(' ').slice(1).join(' ') || 'News'}</span></h1>
          <p className="text-xl md:text-2xl font-light text-zinc-300">{t('competitionResults')}</p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-zinc-900 border-b border-zinc-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-pink-500 text-white'
                    : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                }`}
              >
                {categoryMap[category] || category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-20 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredNews.map((item) => (
              <Link
                key={item.id}
                to={`/news/${item.id}`}
                className="group bg-zinc-900 border border-zinc-800 overflow-hidden hover:border-pink-500/50 transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4 bg-pink-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {categoryMap[item.category] || item.category}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-zinc-500 mb-3">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>{item.date}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white group-hover:text-pink-400 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-zinc-400 line-clamp-3">{item.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export const NewsDetail = () => {
  const { id } = useParams();
  const article = news.find((item) => item.id === parseInt(id));
  const { t } = useLanguage();

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">{t('articleNotFound')}</h2>
          <Link to="/news" className="text-gray-600 hover:text-gray-900">
            {t('backToNews')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] flex items-end"
        style={{
          backgroundImage: `url(${article.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        <div className="relative z-10 container mx-auto px-4 pb-12 text-white">
          <Link
            to="/news"
            className="inline-flex items-center space-x-2 mb-6 text-white hover:text-gray-300 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t('backToNews')}</span>
          </Link>
          <div className="flex items-center space-x-4 mb-4">
            <span className="bg-white text-gray-900 px-3 py-1 rounded-full text-sm font-semibold">
              {article.category}
            </span>
            <span className="flex items-center text-sm">
              <Calendar className="w-4 h-4 mr-2" />
              {article.date}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 max-w-4xl">{article.title}</h1>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8 font-light">{article.excerpt}</p>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">{article.content}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default News;